import { AxiosError } from 'axios';
import deepcopy from 'deepcopy';
import { useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { ColorResult } from 'react-color';

import { ReactSingleSelectInput, SelectForm } from '@/components/uiParts';
import {
  getFilePath,
  reRequestMessage,
  validateMessage,
} from '@/domain/models/Image';
import { SitePayTypeSettingKey } from '@/domain/models/Order';
import { ORDER_METHOD, OrderMethod } from '@/domain/models/Order/orderMethod';
import {
  HandleChangeIsCustomOrderTypeDisplayName,
  HandleChangeOrderTypeDisplayName,
} from '@/pages/camel-order/sites/components/OrderMethodDisplayNameSettingFormItem/type';
import {
  CustomField,
  gmoShopForUpdate,
  IMAGE_RULE,
  initializeSites,
  PayTypeMap,
  Site,
  taxRoundTypeMap,
  validateImageCapacity,
} from '@/pages/camel-order/sites/service';
import {
  SiteFormError,
  validateCustomFieldDescription,
  validateCustomFieldMaximum,
  validateCustomFieldMinimum,
  validateCustomFieldName,
  validateCustomFieldRegex,
  validateDeliveryMinimumAmount,
  validateDescription,
  validateFaQUrl,
  validateGmoShopContactEmail,
  validateGmoShopContactName,
  validateGmoShopContactOpeningAndClosingHours,
  validateGmoShopContactPhone,
  validateGmoShopName,
  validateGmoShopNameAlphabet,
  validateGmoShopNameKana,
  validateGmoShopNameShort,
  validateInquiryUrl,
  validateLinkUrl,
  validateNotificationText,
  validateOrderTypeDisplayName,
  validatePassword,
  validatePrivacyUrl,
  validateSctlUrl,
  validateTermUrl,
  validateTitle,
} from '@/pages/camel-order/sites/validate';
import { useCamelOrderCreateS3Image } from '@/services/CamelOrder/hooks/createS3Image/createS3Image';
import { useCamelOrderPresigned } from '@/services/CamelOrder/hooks/presignedUrl/usePresigned';
import {
  CamelOrderPayType,
  DisplaySpecialInstruction,
  IsAuthAvailable,
  SiteStatus,
  TaxRoundType,
  useSiteList,
} from '@/services/Site/hooks/list/useSiteList';
import { SiteUpdateParams } from '@/services/Site/hooks/update/useSiteUpdate';
import { useSiteUpdate } from '@/services/Site/hooks/update/useSiteUpdate';

export type CanUseDeliveryServices = 'uber';
export type SoldBySolo = '0' | '1';
export type SalesState = 'available' | 'not_available';
export type Page = 'new' | 'edit';

export const changeSiteStatus = (
  sites: Site[],
  siteIndex: number,
  status: SiteStatus
): Site[] =>
  sites.map((site, index) =>
    index === siteIndex ? { ...site, status } : site
  );

export const useCamelOrderSiteForm = () => {
  const [sites, setSites] = useState<Site[]>([]);
  const [initialSites, setInitialSites] = useState<Site[]>([]);
  const [siteIndex, setSiteIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowPassword, setIsShowPassword] = useState<boolean>(false);
  const errorObj = { message: '', isError: false, isTouched: false };
  const [error, setError] = useState<SiteFormError>({
    title: { ...errorObj },
    description: { ...errorObj },
    password: { ...errorObj },
    notificationText: { ...errorObj },
    faqUrl: { ...errorObj },
    inquiryUrl: { ...errorObj },
    termUrl: { ...errorObj },
    sctlUrl: { ...errorObj },
    privacyUrl: { ...errorObj },
    customFieldName: { ...errorObj },
    customFieldMinimum: { ...errorObj },
    customFieldMaximum: { ...errorObj },
    customFieldDescription: { ...errorObj },
    customFieldRegex: { ...errorObj },
    deliveryMinimumAmount: { ...errorObj },
    gmoShopName: { ...errorObj },
    gmoShopNameKana: { ...errorObj },
    gmoShopNameAlphabet: { ...errorObj },
    gmoShopNameShort: { ...errorObj },
    gmoShopContactName: { ...errorObj },
    gmoShopContactEmail: { ...errorObj },
    gmoShopContactPhone: { ...errorObj },
    gmoShopContactOpeningHours: { ...errorObj },
    gmoShopContactClosingHours: { ...errorObj },
    orderTypeDisplayNameByTakeoutText: { ...errorObj },
    orderTypeDisplayNameByTakeoutButtonText: { ...errorObj },
    orderTypeDisplayNameByEatInText: { ...errorObj },
    orderTypeDisplayNameByEatInButtonText: { ...errorObj },
    orderTypeDisplayNameByDeliveryText: { ...errorObj },
    orderTypeDisplayNameByDeliveryButtonText: { ...errorObj },
  });

  const handleChangeSiteUrl = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const index = Number(e.target.value);
    setSiteIndex(index);
  };

  const handleChangeStatus = (newStatus: React.ReactText) => {
    setSites(
      changeSiteStatus(sites, siteIndex, Number(newStatus) as SiteStatus)
    );
  };

  const handleInputPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const copySites = [...sites];
    copySites[siteIndex].password = newValue;
    setError(validatePassword(newValue, error));
    setSites(copySites);
  };

  const togglePasswordVisibility = () => {
    setIsShowPassword((prev) => !prev);
  };

  const handleChangePayTypeSetting = (
    payType: PayTypeMap[],
    settingKey: SitePayTypeSettingKey,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const copySites = [...sites];
    const isChecked = e.target.checked;
    const value = e.target.value as CamelOrderPayType;

    // 保存時の支払い方式と比較し、現在表示されている支払い方式のみを更新対象とする
    const availablePayTypes = payType.map((p) => p.value);

    let prevItems =
      copySites[siteIndex].pay_type_settings.find(
        (v) => v.setting_key === settingKey
      )?.pay_types ?? [];

    prevItems = prevItems.filter((item) => availablePayTypes.includes(item));

    const newPayItems = isChecked
      ? [...prevItems, value]
      : prevItems.filter((item) => item !== value);

    const payTypeSettingIndex = copySites[
      siteIndex
    ].pay_type_settings.findIndex((v) => v.setting_key === settingKey);

    if (payTypeSettingIndex === -1) {
      copySites[siteIndex].pay_type_settings.push({
        setting_key: settingKey,
        pay_types: newPayItems,
      });
    } else {
      copySites[siteIndex].pay_type_settings[payTypeSettingIndex].pay_types =
        newPayItems;
    }

    setSites(copySites);
  };

  const handleChangeOrderMethod = (e: React.ChangeEvent<HTMLInputElement>) => {
    const copySites = [...sites];
    const isChecked = e.target.checked;
    const value = e.target.value as OrderMethod;
    const prevItems = copySites[siteIndex].order_methods;
    const newValues = isChecked
      ? [...prevItems, value]
      : prevItems.filter((item) => item !== value);
    copySites[siteIndex].order_methods = newValues;
    setSites(copySites);
  };

  const handleChangeDeliveryCartItemLimit = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const copySites = [...sites];
    const value = e.target.value;
    copySites[siteIndex].delivery_cart_item_limit =
      value === '' ? null : Number(value);
    setSites(copySites);
  };

  const handleChangeTaxRoundType = (value: string): ReactSingleSelectInput => {
    return {
      value: taxRoundTypeMap.find((item) => item.value === value),
      options: taxRoundTypeMap,
      onChange: (newForm: SelectForm | null) => {
        const copySites = [...sites];
        const prevItems = copySites[siteIndex].tax_round_type;
        if (newForm === null || newForm.value === prevItems) return;
        copySites[siteIndex].tax_round_type = newForm.value as TaxRoundType;
        setSites(copySites);
      },
      onReset: () => {
        return null;
      },
    };
  };

  const handleChangeLogoImage = (file?: File) => {
    if (file === undefined) return;
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      const fr = new FileReader();
      fr.onload = async () => {
        const capacity = file.size;
        const base64Url = fr.result as string; /* base64の形で取得 */
        const maxSize = IMAGE_RULE.LOGO.maxSize;
        if (validateImageCapacity.check(capacity, maxSize)) {
          return alert(validateImageCapacity.message(maxSize));
        }
        const now = new Date();
        const filePath = getFilePath(now);
        const { url } = await useCamelOrderPresigned({
          filePath,
          contentType: file.type,
        });
        const copySites = [...sites];
        copySites[siteIndex].logo.image_url = base64Url;
        copySites[siteIndex].logo.presigned_url = url;
        copySites[siteIndex].logo.file = file;
        setSites(copySites);
      };
      fr.readAsDataURL(file);
    };
    img.src = URL.createObjectURL(file);
  };

  const handleChangeFaviconImage = (file?: File) => {
    if (file === undefined) return;
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      const fr = new FileReader();
      fr.onload = async () => {
        const capacity = file.size;
        const base64Url = fr.result as string; /* base64の形で取得 */
        const maxSize = IMAGE_RULE.FAVICON.maxSize;
        if (img.width !== img.height) {
          return alert('縦と横の画像サイズを同じにしてください。');
        }
        if (validateImageCapacity.check(capacity, maxSize)) {
          return alert(validateImageCapacity.message(maxSize));
        }
        const now = new Date();
        const filePath = getFilePath(now);
        const { url } = await useCamelOrderPresigned({
          filePath,
          contentType: file.type,
        });
        const copySites = [...sites];
        copySites[siteIndex].favicon.image_url = base64Url;
        copySites[siteIndex].favicon.presigned_url = url;
        copySites[siteIndex].favicon.file = file;
        setSites(copySites);
      };
      fr.readAsDataURL(file);
    };
    img.src = URL.createObjectURL(file);
  };

  const handleClearImage = (key: 'logo' | 'favicon') => {
    const copySites = [...sites];
    copySites[siteIndex][key].image_url = '';
    copySites[siteIndex][key].presigned_url = '';
    setSites(copySites);
  };

  const handleChangeIsCustomOrderTypeDisplayName: HandleChangeIsCustomOrderTypeDisplayName =
    (orderMethod, isCustom) => {
      const copySites = [...sites];
      copySites[siteIndex].custom_order_method_text[orderMethod].is_custom =
        isCustom;
      setSites(copySites);
    };

  const handleChangeOrderTypeDisplayName: HandleChangeOrderTypeDisplayName = (
    orderMethod,
    isCustom,
    type,
    value
  ) => {
    const copySites = [...sites];
    copySites[siteIndex].custom_order_method_text[orderMethod][type] = value;
    setError(
      validateOrderTypeDisplayName(value, type, orderMethod, isCustom, error)
    );
    setSites(copySites);
  };

  const handleChangeColor = (color: ColorResult) => {
    const newValue = color.hex;
    const copySites = [...sites];
    copySites[siteIndex].primary_color = newValue;
    setSites(copySites);
  };

  const handleInputName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const copySites = [...sites];
    copySites[siteIndex].gmo_shop.name = newValue;
    setError(validateGmoShopName(newValue, error));
    setSites(copySites);
  };

  const handleInputNameKana = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const copySites = [...sites];
    copySites[siteIndex].gmo_shop.name_kana = newValue;
    setError(validateGmoShopNameKana(newValue, error));
    setSites(copySites);
  };

  const handleInputNameAlphabet = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const copySites = [...sites];
    copySites[siteIndex].gmo_shop.name_alphabet = newValue;
    setError(validateGmoShopNameAlphabet(newValue, error));
    setSites(copySites);
  };

  const handleInputNameShort = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const copySites = [...sites];
    copySites[siteIndex].gmo_shop.name_short = newValue;
    setError(validateGmoShopNameShort(newValue, error));
    setSites(copySites);
  };

  const handleInputContactName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const copySites = [...sites];
    copySites[siteIndex].gmo_shop.contact_name = newValue;
    setError(validateGmoShopContactName(newValue, error));
    setSites(copySites);
  };

  const handleInputContactEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const copySites = [...sites];
    copySites[siteIndex].gmo_shop.contact_email = newValue;
    setError(validateGmoShopContactEmail(newValue, error));
    setSites(copySites);
  };

  const handleInputContactPhone = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const copySites = [...sites];
    copySites[siteIndex].gmo_shop.contact_phone = newValue;
    setError(validateGmoShopContactPhone(newValue, error));
    setSites(copySites);
  };

  const handleChangeContactOpeningHour = (newHour: string) => {
    const copySites = [...sites];
    const currentStartHours =
      copySites[siteIndex].gmo_shop.contact_opening_hours;
    const currentEndHours = copySites[siteIndex].gmo_shop.contact_closing_hours;
    const [, minutes] = currentStartHours.split(':');
    const newHours = `${newHour.padStart(2, '0')}:${minutes}`;
    copySites[siteIndex].gmo_shop.contact_opening_hours = newHours;
    setError(
      validateGmoShopContactOpeningAndClosingHours(
        newHours,
        currentEndHours,
        error
      )
    );
    setSites(copySites);
  };

  const handleChangeContactOpeningMinute = (newMinute: string) => {
    const copySites = [...sites];
    const currentStartHours =
      copySites[siteIndex].gmo_shop.contact_opening_hours;
    const currentEndHours = copySites[siteIndex].gmo_shop.contact_closing_hours;
    const [hours] = currentStartHours.split(':');

    const newHours = `${hours}:${newMinute.padStart(2, '0')}`;

    copySites[siteIndex].gmo_shop.contact_opening_hours = newHours;
    setError(
      validateGmoShopContactOpeningAndClosingHours(
        newHours,
        currentEndHours,
        error
      )
    );
    setSites(copySites);
  };

  const handleChangeContactClosingHour = (newHour: string) => {
    const copySites = [...sites];
    const currentStartHours =
      copySites[siteIndex].gmo_shop.contact_opening_hours;
    const currenEndtHours = copySites[siteIndex].gmo_shop.contact_closing_hours;
    const [, minutes] = currenEndtHours.split(':');
    const newHours = `${newHour.padStart(2, '0')}:${minutes}`;
    copySites[siteIndex].gmo_shop.contact_closing_hours = newHours;
    setError(
      validateGmoShopContactOpeningAndClosingHours(
        currentStartHours,
        newHours,
        error
      )
    );

    setSites(copySites);
  };

  const handleChangeContactClosingMinute = (newMinute: string) => {
    const copySites = [...sites];
    const currentStartHours =
      copySites[siteIndex].gmo_shop.contact_opening_hours;
    const currentEndHours = copySites[siteIndex].gmo_shop.contact_closing_hours;
    const [hours] = currentEndHours.split(':');

    const newHours = `${hours}:${newMinute.padStart(2, '0')}`;

    copySites[siteIndex].gmo_shop.contact_closing_hours = newHours;
    setError(
      validateGmoShopContactOpeningAndClosingHours(
        currentStartHours,
        newHours,
        error
      )
    );
    setSites(copySites);
  };

  const handleInputFaQUrl = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFaqUrl = e.target.value;
    const copySites = [...sites];
    copySites[siteIndex].faq_url = newFaqUrl;
    setError(validateFaQUrl(newFaqUrl, error));
    setSites(copySites);
  };

  const handleInputInquiryUrl = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newInquiryUrl = e.target.value;
    const copySites = [...sites];
    copySites[siteIndex].inquiry_url = newInquiryUrl;
    setError(validateInquiryUrl(newInquiryUrl, error));
    setSites(copySites);
  };

  const handleInputTermUrl = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTermUrl = e.target.value;
    const copySites = [...sites];
    copySites[siteIndex].term_url = newTermUrl;
    setError(validateTermUrl(newTermUrl, error));
    setSites(copySites);
  };

  const handleInputSctlUrl = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSctlUrl = e.target.value;
    const copySites = [...sites];
    copySites[siteIndex].sctl_url = newSctlUrl;
    setError(validateSctlUrl(newSctlUrl, error));
    setSites(copySites);
  };

  const handleInputPrivacyUrl = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPrivacyUrl = e.target.value as string;
    const copySites = [...sites];
    copySites[siteIndex].privacy_url = newPrivacyUrl;
    setError(validatePrivacyUrl(newPrivacyUrl, error));
    setSites(copySites);
  };

  const handleInputTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value as string;
    const copySites = [...sites];
    copySites[siteIndex].title = newTitle;
    setError(validateTitle(newTitle, error));
    setSites(copySites);
  };

  const handleInputDescription = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const newDescription = e.target.value;
    const copySites = [...sites];
    copySites[siteIndex].description = newDescription;
    setError(validateDescription(newDescription, error));
    setSites(copySites);
  };

  const handleInputNotificationText = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const newNotificationText = e.target.value;
    const copySites = [...sites];
    copySites[siteIndex].notification_text = newNotificationText;
    setError(validateNotificationText(newNotificationText, error));
    setSites(copySites);
  };

  const handleChangeBannerImage = (file: File, bannerIndex: number) => {
    if (file === undefined) return;
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);

      const fr = new FileReader();
      fr.onload = async () => {
        const base64Url = fr.result as string; /* base64の形で取得 */
        const { size, type } = file;
        const maxSize = IMAGE_RULE.BANNER.maxSize;
        if (validateImageCapacity.check(size, maxSize)) {
          return alert(validateImageCapacity.message(maxSize));
        }
        const now = new Date();
        const filePath = getFilePath(now);
        const { url } = await useCamelOrderPresigned({
          filePath,
          contentType: type,
        });
        const copySites = [...sites];
        copySites[siteIndex].site_banners[bannerIndex].image_url = base64Url;
        copySites[siteIndex].site_banners[bannerIndex].presigned_url = url;
        copySites[siteIndex].site_banners[bannerIndex].file = file;
        setSites(copySites);
      };
      fr.readAsDataURL(file);
    };
    img.src = URL.createObjectURL(file);
  };
  const handleChangeBanneerLinkUrl = (
    e: React.ChangeEvent<HTMLInputElement>,
    siteIndex: number,
    bannerIndex: number
  ) => {
    if (e.target.value === '') {
      const copySites = [...sites];
      copySites[siteIndex].site_banners[bannerIndex].link_url = null;
      setSites(copySites);
      return;
    }

    const newLinkUrl = e.target.value;

    const copySites = [...sites];
    copySites[siteIndex].site_banners[bannerIndex].link_url = newLinkUrl;
    setSites(copySites);
  };

  const handleInputGoogleTagId = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const newGoogleTagId = e.target.value as string;
    const copySites = [...sites];
    copySites[siteIndex].google_tag_ids[index] = newGoogleTagId;
    setSites(copySites);
  };

  const handleAddGoogleTagId = () => {
    const copySites = [...sites];
    copySites[siteIndex].google_tag_ids.push('');
    setSites(copySites);
  };

  const handleRemoveGoogleTagId = (googleTagIdIndex: number) => {
    const copySites = [...sites];
    const googleTagIds = copySites[siteIndex].google_tag_ids.filter(
      (_, index) => {
        return index !== googleTagIdIndex;
      }
    );
    copySites[siteIndex].google_tag_ids = googleTagIds;
    setSites(copySites);
  };

  const handleDeleteBannerImage = (bannerIndex: number) => {
    const copySites = [...sites];
    copySites[siteIndex].site_banners = copySites[
      siteIndex
    ].site_banners.filter((_, index) => {
      return bannerIndex !== index;
    });
    setSites(copySites);
  };

  const handleAddCustomField = () => {
    const copySites = [...sites];
    const takeout = sites[siteIndex].order_methods.includes('TAKE_OUT');
    const eatIn = sites[siteIndex].order_methods.includes('EAT_IN');
    const delivery = sites[siteIndex].order_methods.includes('DELIVERY');
    copySites[siteIndex].custom_fields = [
      ...copySites[siteIndex].custom_fields,
      {
        id: '',
        site_id: sites[siteIndex].id,
        name: '',
        takeout,
        eat_in: eatIn,
        delivery,
        required: false,
        custom_field_type: 'TEXT',
        description: '',
      },
    ];
    setSites(copySites);
  };
  const handleChangeDeliveryMinimumAmount = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const copySites = [...sites];
    const newValue = e.target.value;
    const pattern = /^([0-9]*)$/;
    if (!pattern.test(newValue)) return;
    const deliveryMinimumAmount = e.target.value;
    setError(validateDeliveryMinimumAmount(deliveryMinimumAmount, error));
    copySites[siteIndex].delivery_minimum_amount = Number(
      deliveryMinimumAmount
    );
    setSites(copySites);
  };

  const handleRemoveCustomField = (customFieldIndex: number) => {
    const copySites = [...sites];
    const customFields = copySites[siteIndex].custom_fields.filter(
      (_, index) => {
        return index !== customFieldIndex;
      }
    );
    copySites[siteIndex].custom_fields = customFields;
    setSites(copySites);
  };

  const isOrderMethodChecked = (
    optionValue: OrderMethod,
    customField: CustomField
  ) => {
    return (
      (optionValue === 'EAT_IN' && customField['eat_in'] === true) ||
      (optionValue === 'TAKE_OUT' && customField['takeout'] === true) ||
      (optionValue === 'DELIVERY' && customField['delivery'] === true)
    );
  };

  const handleChangeCustomField = (
    customFieldIndex: number,
    field: keyof CustomField,
    value: string
  ) => {
    const copySites = [...sites];

    if (field === 'minimum') {
      const maxMum =
        copySites[siteIndex].custom_fields[customFieldIndex].maximum;

      // 空文字列の場合はnullを設定し、それ以外は数値に変換
      const numValue =
        value === '' ? null : isNaN(Number(value)) ? null : Number(value);
      (copySites[siteIndex].custom_fields[customFieldIndex][field] as
        | number
        | null) = numValue;

      setError(validateCustomFieldMinimum(value, error, maxMum));
    } else if (field === 'maximum') {
      const minMum =
        copySites[siteIndex].custom_fields[customFieldIndex].minimum;

      // 空文字列の場合はnullを設定し、それ以外は数値に変換
      const numValue =
        value === '' ? null : isNaN(Number(value)) ? null : Number(value);
      (copySites[siteIndex].custom_fields[customFieldIndex][field] as
        | number
        | null) = numValue;

      setError(validateCustomFieldMaximum(value, error, minMum));
    } else if (field === 'required') {
      const isChecked = value === '1';
      copySites[siteIndex].custom_fields[customFieldIndex][field] = isChecked;
    } else {
      if (field === 'name') {
        setError(validateCustomFieldName(value as string, error));
      }
      if (field === 'description') {
        setError(validateCustomFieldDescription(value as string, error));
      }
      if (field === 'regex') {
        setError(validateCustomFieldRegex(value as string, error));
      }
      (copySites[siteIndex].custom_fields[customFieldIndex][
        field
      ] as React.ReactText) = value;
    }
    setSites(copySites);
  };

  const handleChangeCustomFieldOrderMethod = (
    isChecked: boolean,
    customFieldIndex: number,
    optionValue: string
  ) => {
    const field = (() => {
      switch (optionValue) {
        case ORDER_METHOD.EAT_IN:
          return 'eat_in';
        case ORDER_METHOD.TAKE_OUT:
          return 'takeout';
        case ORDER_METHOD.DELIVERY:
          return 'delivery';
        default:
          return '';
      }
    })();
    if (field === '') return;

    const copySites = [...sites];

    copySites[siteIndex].custom_fields[customFieldIndex][field] = isChecked;
    setSites(copySites);
  };

  const handleAddSiteBannerImage = () => {
    const copySites = [...sites];
    copySites[siteIndex].site_banners = [
      ...copySites[siteIndex].site_banners,
      {
        id: '',
        image_url: '',
        sort_index: 1,
        link_url: null,
        presigned_url: '',
      },
    ];
    setSites(copySites);
  };

  const handleChangedisplaySpecialInstruction = (newValue: React.ReactText) => {
    const copySites = [...sites];
    copySites[siteIndex].display_special_instruction = Number(
      newValue
    ) as DisplaySpecialInstruction;
    setSites(copySites);
  };

  const handleChangeIsAuthAvailable = (newValue: React.ReactText) => {
    const copySites = [...sites];
    copySites[siteIndex].is_auth_available = Number(
      newValue
    ) as IsAuthAvailable;
    setSites(copySites);
  };

  const handleChangeUseThreeDSecure2 = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const copySites = [...sites];
    copySites[siteIndex].use_three_d_secure2 = e.target.checked;
    setSites(copySites);
  };

  const handleOnDragEnd = (result: DropResult) => {
    const copySites = [...sites];
    const targetSiteBanners = copySites[siteIndex].site_banners;
    const [reorderedItem] = targetSiteBanners.splice(result.source.index, 1);
    targetSiteBanners.splice(result.destination?.index ?? 0, 0, reorderedItem);
    setSites(copySites);
  };

  const handleUpdateSite = async () => {
    const site = sites[siteIndex];
    if (!confirm(`${site.url}のサイト情報を更新します。よろしいですか？`))
      return;
    setIsLoading(true);
    // urlもいらない。
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { url: _, ...omitSite } = site;
    type Image = {
      presigned_url: string;
      file: File;
    };
    const images: Image[] = [];
    if (site.logo.presigned_url !== '' && site.logo.file !== undefined) {
      images.push({
        presigned_url: site.logo.presigned_url,
        file: site.logo.file,
      });
    }
    if (site.favicon.presigned_url !== '' && site.favicon.file !== undefined) {
      images.push({
        presigned_url: site.favicon.presigned_url,
        file: site.favicon.file,
      });
    }
    let bannerError = false;
    site.site_banners.forEach((banner) => {
      if (banner.presigned_url !== '' && banner.file !== undefined) {
        images.push({
          presigned_url: banner.presigned_url,
          file: banner.file,
        });
      }
      if (banner.link_url !== null) {
        if (validateLinkUrl(banner.link_url)) {
          alert(`${banner.sort_index}番目のバナーのリンクURLが不正です。`);
          bannerError = true;
          return;
        }
      }
    });

    if (bannerError) return setIsLoading(false);
    let isError = false;

    for (const image of images) {
      try {
        await useCamelOrderCreateS3Image(image.presigned_url, image.file);
      } catch (err) {
        isError = true;
        const asserErr = err as AxiosError;
        if (asserErr.response?.status === 403) {
          alert(reRequestMessage(image.file.name));
        } else {
          alert(validateMessage(image.file.name));
        }
        // break; // 全ての画像でエラーを出した方がいいと思うのでコメントアウト
      }
    }
    if (isError) return setIsLoading(false);
    const faviconUrl =
      site.favicon.presigned_url !== ''
        ? site.favicon.presigned_url.split('?')[0]
        : site.favicon.image_url;
    const params: SiteUpdateParams = {
      ...omitSite,
      pay_type_settings: site.pay_type_settings.map((setting) => ({
        setting_key: setting.setting_key,
        pay_types: setting.pay_types,
      })),
      faq_url: site.faq_url !== '' ? site.faq_url : null,
      inquiry_url: site.inquiry_url !== '' ? site.inquiry_url : null,
      logo_url:
        site.logo.presigned_url !== ''
          ? site.logo.presigned_url.split('?')[0]
          : site.logo.image_url,
      favicon_url: faviconUrl !== '' ? faviconUrl : null,
      google_tag_ids: site.google_tag_ids.filter(
        (googleTagId) => googleTagId !== ''
      ),
      display_special_instruction: site.display_special_instruction === 1,
      is_auth_available: site.is_auth_available === 1,
      use_three_d_secure2: site.use_three_d_secure2,
      site_banners: site.site_banners.map((banner, index) => {
        return {
          image_url:
            banner.presigned_url !== ''
              ? banner.presigned_url.split('?')[0]
              : banner.image_url,
          sort_index: index + 1,
          link_url: banner.link_url,
        };
      }),
      gmo_shop: gmoShopForUpdate(site.pay_type_settings, site.gmo_shop),
      custom_order_method_text: {
        delivery: {
          is_custom: site.custom_order_method_text.delivery.is_custom === '1',
          button_text: site.custom_order_method_text.delivery.button_text,
          text: site.custom_order_method_text.delivery.text,
        },
        eat_in: {
          is_custom: site.custom_order_method_text.eat_in.is_custom === '1',
          button_text: site.custom_order_method_text.eat_in.button_text,
          text: site.custom_order_method_text.eat_in.text,
        },
        takeout: {
          is_custom: site.custom_order_method_text.takeout.is_custom === '1',
          button_text: site.custom_order_method_text.takeout.button_text,
          text: site.custom_order_method_text.takeout.text,
        },
      },
      custom_fields: site.custom_fields,
    };
    try {
      await useSiteUpdate(site.id, params);
      const resSites = await useSiteList();
      const buildSites = initializeSites(resSites);
      const cooyInitialSites = deepcopy(initialSites);
      cooyInitialSites[siteIndex] = buildSites[siteIndex];
      setInitialSites(cooyInitialSites);
      setIsLoading(false);
      alert('サイト情報を更新しました。');
    } catch (e) {
      setIsLoading(false);
    }
  };

  const initializeSitePage = async () => {
    setIsLoading(true);
    const resSites = await useSiteList();
    const buildSites = initializeSites(resSites);
    setSites(buildSites);
    const initialSites = deepcopy(buildSites);
    setIsLoading(false);
    setInitialSites(initialSites);
  };

  return {
    initialSites,
    sites,
    siteIndex,
    isLoading,
    error,
    handleChangeSiteUrl,
    handleChangeStatus,
    handleChangePayTypeSetting,
    handleChangeOrderMethod,
    handleChangeTaxRoundType,
    handleChangeLogoImage,
    handleChangeFaviconImage,
    handleClearImage,
    handleChangeColor,
    handleInputPassword,
    isShowPassword,
    togglePasswordVisibility,
    handleInputName,
    handleInputNameKana,
    handleInputNameAlphabet,
    handleInputNameShort,
    handleInputContactName,
    handleInputContactEmail,
    handleInputContactPhone,
    handleChangeContactOpeningHour,
    handleChangeContactOpeningMinute,
    handleChangeContactClosingHour,
    handleChangeContactClosingMinute,
    handleInputFaQUrl,
    handleInputInquiryUrl,
    handleInputTermUrl,
    handleInputSctlUrl,
    handleInputPrivacyUrl,
    handleInputTitle,
    handleInputDescription,
    handleInputGoogleTagId,
    handleAddGoogleTagId,
    handleRemoveGoogleTagId,
    handleRemoveCustomField,
    isOrderMethodChecked,
    handleChangeCustomField,
    handleChangeCustomFieldOrderMethod,
    handleChangeBannerImage,
    handleDeleteBannerImage,
    handleOnDragEnd,
    handleUpdateSite,
    handleAddSiteBannerImage,
    handleChangedisplaySpecialInstruction,
    handleChangeIsAuthAvailable,
    handleInputNotificationText,
    initializeSitePage,
    handleChangeBanneerLinkUrl,
    handleChangeIsCustomOrderTypeDisplayName,
    handleChangeOrderTypeDisplayName,
    handleAddCustomField,
    handleChangeDeliveryMinimumAmount,
    handleChangeDeliveryCartItemLimit,
    handleChangeUseThreeDSecure2,
  };
};
